import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AddClientModal from "../../../components/domains/advisor-portal/add-new-client/AddClientModal";
import Breadcrumbs from "../../../components/shared/Breadcrumbs";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { PieChart, Pie, Cell } from "recharts";
import "react-circular-progressbar/dist/styles.css";
import { apiConfig } from "../../../utils/apiConfig";
import { useAuth } from "../../../contexts/AuthContext";
import EmailComposerModal from "../../../components/domains/advisor-portal/EmailComposerModal";
import CallConfirmationModal from "../../../components/domains/advisor-portal/CallConfirmationModal";
import { Search } from "lucide-react";

import logger from '../../../utils/logger';

// Portfolio data structure for risk exposure
const portfolioData = [
  { name: "Cash & Equivalents", percentage: 20, color: "#00FF00" },
  { name: "CDs/Fixed Annuities", percentage: 15, color: "#ADFF2F" },
  { name: "Low-Risk Bonds", percentage: 20, color: "#FFFF00" },
  { name: "ETFs/Mutual Funds", percentage: 10, color: "#FFA500" },
  { name: "Equities (Blue-Chip)", percentage: 15, color: "#FF7F50" },
  { name: "High-Yield Debt", percentage: 10, color: "#FF4500" },
  { name: "Structured Notes", percentage: 5, color: "#FF6347" },
  { name: "Growth Equities", percentage: 3, color: "#FF0000" },
  { name: "Private Equity", percentage: 1, color: "#8B0000" },
  { name: "Cryptocurrency", percentage: 1, color: "#800080" },
  { name: "Alternative Investments", percentage: 0.5, color: "#6A0DAD" },
  { name: "Speculative Investments", percentage: 0.5, color: "#4B0082" },
];

const getHealthScoreColor = (score) => {
  if (score >= 95) return { color: "#008000", label: "Excellent" };
  if (score >= 85) return { color: "#32CD32", label: "Very Good" };
  if (score >= 75) return { color: "#90EE90", label: "Good" };
  if (score >= 70) return { color: "#ADFF2F", label: "Acceptable" };
  if (score >= 60) return { color: "#FFD700", label: "Fair" };
  if (score >= 45) return { color: "#FF8C42", label: "Weak" };
  return { color: "#FF4C4C", label: "Critical" };
};

const HealthScore = ({ score }) => {
  const { color, label } = getHealthScoreColor(score);

  return (
    <div className="flex items-center w-[120px]">
      <div className="w-[40px] h-[40px] flex-shrink-0">
        <CircularProgressbar
          value={score}
          text={`${Math.round(score)}`}
          styles={buildStyles({
            rotation: 0,
            strokeLinecap: "round",
            textSize: "32",
            pathTransitionDuration: 0.5,
            pathColor: color,
            textColor: "currentColor",
            trailColor: "rgba(200, 200, 200, 0.3)",
            backgroundColor: "transparent",
          })}
          className="text-gray-900 dark:text-gray-100"
        />
      </div>
      <div className="ml-2 flex flex-col min-w-0">
        <div className="text-sm font-medium truncate" style={{ color }}>
          {label}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">Health Score</div>
      </div>
    </div>
  );
};

const RiskExposureChart = ({ riskScore }) => {
  const generatePortfolioData = (score) => {
    let data = [...portfolioData];
    if (score < 50) {
      data[0].percentage = 40;
      data[1].percentage = 30;
      data[2].percentage = 20;
      data[3].percentage = 10;
      data.slice(4).forEach((item) => (item.percentage = 0));
    } else if (score < 70) {
      data[0].percentage = 20;
      data[1].percentage = 20;
      data[2].percentage = 20;
      data[3].percentage = 20;
      data[4].percentage = 15;
      data[5].percentage = 5;
      data.slice(6).forEach((item) => (item.percentage = 0));
    } else {
      data = data.map((item) => ({ ...item, percentage: 8.33 }));
    }
    return data;
  };

  const chartData = generatePortfolioData(riskScore);
  const COLORS = chartData.map((data) => data.color);

  return (
    <div className="flex items-center w-[130px]">
      <div className="w-[36px] h-[36px] relative flex-shrink-0">
        <div
          className="absolute inset-0"
          style={{ transform: "translate(-4px, -4px)" }}
        >
          <PieChart width={44} height={44}>
            <Pie
              data={chartData}
              cx={18}
              cy={18}
              innerRadius={12}
              outerRadius={20}
              dataKey="percentage"
              startAngle={90}
              endAngle={-270}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index]} />
              ))}
            </Pie>
          </PieChart>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{riskScore}</span>
        </div>
      </div>
      <div className="ml-2 flex flex-col min-w-0">
        <div className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">
          {riskScore >= 75
            ? "Aggressive"
            : riskScore >= 50
              ? "Moderate"
              : "Conservative"}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">Risk Profile</div>
      </div>
    </div>
  );
};

// Add this with the other utility functions at the top
const generateHealthScore = () => {
  const rand = Math.random();
  if (rand < 0.02) return Math.floor(Math.random() * 45);
  if (rand < 0.05) return Math.floor(Math.random() * 15) + 45;
  if (rand < 0.1) return Math.floor(Math.random() * 10) + 60;
  if (rand < 0.85) return Math.floor(Math.random() * 20) + 70;
  return Math.floor(Math.random() * 5) + 95;
};

// API configuration is now centralized in apiConfig.js

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "";
  return Math.floor(
    (new Date() - new Date(dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000),
  );
};

const generateAchievements = (healthScore) => {
  const achievements = [];
  if (healthScore >= 95) {
    achievements.push(
      ...["Fully Integrated", "Top 1% Engaged", "Tax Optimizer"]
        .sort(() => Math.random() - 0.5)
        .slice(0, 2),
    );
  } else if (healthScore >= 85) {
    achievements.push(
      ...["Budget Expert", "Event Promoter", "Tax Optimizer"]
        .sort(() => Math.random() - 0.5)
        .slice(0, 1),
    );
  }
  return achievements;
};

const generateUpcomingTasks = (member) => {
  const tasks = [];
  const age = member?.dateOfBirth ? calculateAge(member.dateOfBirth) : null;

  if (age) {
    if (age === 59) tasks.push("Turns 59.5 This Year");
    if (age >= 72) tasks.push("RMD Requirement");
    if (age === 64) tasks.push("Starting Medicare");
  }

  const randomTasks = [
    "Rebalance Portfolio",
    "Happy Birthday!",
    "Annual Review Due",
  ];

  if (Math.random() < 0.3) {
    tasks.push(randomTasks[Math.floor(Math.random() * randomTasks.length)]);
  }

  return tasks;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  const cleaned = phoneNumber.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

// Update the getPrimaryAddress function with debug logging
const getPrimaryAddress = (household) => {
  logger.info("[DEBUG] getPrimaryAddress called with household:", household);

  if (!household) {
    logger.info("[DEBUG] No household provided");
    return null;
  }

  // Try householdAddressLinks from the updated structure
  if (
    household.householdAddressLinks &&
    household.householdAddressLinks.length > 0
  ) {
    logger.info(
      "[DEBUG] Found householdAddressLinks:",
      household.householdAddressLinks.length,
    );

    // Look for primary address in the householdAddressLinks
    const primaryLinkItem = household.householdAddressLinks.find(
      (link) => link.is_primary,
    );

    if (primaryLinkItem && primaryLinkItem.Address) {
      logger.info("[DEBUG] Found primary address in householdAddressLinks");
      return {
        ...primaryLinkItem.Address,
        is_primary: true,
        address_type_name:
          primaryLinkItem.AddressType?.address_type_name || "Primary",
      };
    }
  }

  // If we get here, try the old structure
  if (household.addresses && household.addresses.length > 0) {
    logger.info("[DEBUG] Found addresses array:", household.addresses.length);

    // Try to find the primary address first
    const primaryAddress = household.addresses.find(
      (addr) => addr.HouseholdAddresses?.is_primary,
    );

    if (primaryAddress) {
      logger.info("[DEBUG] Found primary address in addresses array");
      return primaryAddress;
    }

    // If there's no marked primary address, just return the first one
    logger.info("[DEBUG] No primary address found, returning first address");
    return household.addresses[0];
  }

  // Try to find an address in the primaryAddress relation
  if (household.primaryAddress) {
    logger.info("[DEBUG] Found primaryAddress relation");
    return household.primaryAddress;
  }

  // If we get here, no address was found
  logger.info("[DEBUG] No address found in any expected location");
  return null;
};

// Add this utility function at the top with other utility functions
const copyTextToClipboard = (text) => {
  // Try to use the Clipboard API first
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => {
        // If clipboard API fails, use the fallback
        return fallbackCopyTextToClipboard(text);
      });
  }
  // Fallback for browsers without clipboard API support
  return Promise.resolve(fallbackCopyTextToClipboard(text));
};

const fallbackCopyTextToClipboard = (text) => {
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Make the textarea out of viewport
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);

    // Select and copy
    textArea.focus();
    textArea.select();
    const successful = document.execCommand("copy");

    // Cleanup
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    logger.error("Fallback clipboard copy failed:", err);
    return false;
  }
};

const Clients = () => {
  // Note: Filters are collapsed by default
  const [showFilters, setShowFilters] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const clientsPerPage = 10;
  const [filters, setFilters] = useState({
    pipelineStage: {
      clients: false,
      futureClients: false,
      retired: false,
      highNetWorth: false,
      rmdRequirement: false,
    },
    lastContacted: {
      thisWeek: false,
      thisMonth: false,
      thisQuarter: false,
      thisYear: false,
    },
    ageRange: {
      min: "",
      max: "",
    },
    state: "",
    householdAssets: {
      min: "",
      max: "",
    },
  });
  const [sortBy, setSortBy] = useState("lastName");
  const navigate = useNavigate();
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { token } = useAuth();

  // Fetch and sort clients
  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!token) {
          throw new Error("No authentication token found");
        }

        // Add include_contact=true and include_risk=true query parameters
        const response = await apiConfig.api.get(
          "/advisor-portal/client-database?include_contact=true&include_risk=true",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        logger.info("API Response:", response.data);

        const processAndSortClients = (clients) => {
          return clients.sort((a, b) => {
            // Get last names from either direct property or nested Name object
            const lastNameA = (
              a.lastName ||
              a.Name?.last_name ||
              ""
            ).toLowerCase();
            const lastNameB = (
              b.lastName ||
              b.Name?.last_name ||
              ""
            ).toLowerCase();

            // If last names are equal, sort by first name
            if (lastNameA === lastNameB) {
              const firstNameA = (
                a.firstName ||
                a.Name?.first_name ||
                ""
              ).toLowerCase();
              const firstNameB = (
                b.firstName ||
                b.Name?.first_name ||
                ""
              ).toLowerCase();
              return firstNameA.localeCompare(firstNameB);
            }

            return lastNameA.localeCompare(lastNameB);
          });
        };

        if (response.data && Array.isArray(response.data.clients)) {
          logger.info(
            "Number of clients received:",
            response.data.clients.length,
          );
          const sortedClients = processAndSortClients(response.data.clients);
          logger.info("Sorted clients:", sortedClients);

          // Log a sample client to check structure
          if (sortedClients.length > 0) {
            logger.info(
              "Sample client structure:",
              JSON.stringify(sortedClients[0], null, 2),
            );
            if (
              sortedClients[0].members &&
              sortedClients[0].members.length > 0
            ) {
              logger.info(
                "Sample member structure:",
                JSON.stringify(sortedClients[0].members[0], null, 2),
              );
              logger.info(
                "Member contact info:",
                sortedClients[0].members[0].contact,
              );

              // Look for contact info in different places
              const sampleMember = sortedClients[0].members[0];
              logger.info("Member direct fields:", {
                email: sampleMember.email,
                phone: sampleMember.phone,
                primary_number: sampleMember.primary_number,
                contact_id: sampleMember.contact_id,
              });

              if (sampleMember.ContactInformation) {
                logger.info(
                  "ContactInformation:",
                  sampleMember.ContactInformation,
                );
              }
            }
          }

          setClients(sortedClients);
        } else if (response.data && Array.isArray(response.data)) {
          logger.info(
            "Number of clients received (root array):",
            response.data.length,
          );
          const sortedClients = processAndSortClients(response.data);
          logger.info("Sorted clients:", sortedClients);

          // Log a sample client to check structure
          if (sortedClients.length > 0) {
            logger.info(
              "Sample client structure (root array):",
              JSON.stringify(sortedClients[0], null, 2),
            );

            // Look for contact info in different places
            logger.info("Client direct fields:", {
              email: sortedClients[0].email,
              phone: sortedClients[0].phone,
              primary_number: sortedClients[0].primary_number,
              contact_id: sortedClients[0].contact_id,
            });

            if (sortedClients[0].ContactInformation) {
              logger.info(
                "ContactInformation:",
                sortedClients[0].ContactInformation,
              );
            }
          }

          setClients(sortedClients);
        } else {
          logger.info("Unexpected response structure:", response.data);
          setClients([]);
        }
      } catch (err) {
        logger.error("Error fetching clients:", err);
        if (err.response?.status === 401) {
          navigate("/login");
        } else {
          setError("Failed to fetch clients");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [navigate, token]);

  const handleClientClick = (householdId, userId) => {
    navigate(
      `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}`,
    );
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (category, key) => {
    setFilters((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: !prev[category][key],
      },
    }));
  };

  const handleRangeChange = (category, field, value) => {
    setFilters((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const handleStateChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      state: e.target.value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      pipelineStage: {
        clients: false,
        futureClients: false,
        retired: false,
        highNetWorth: false,
        rmdRequirement: false,
      },
      lastContacted: {
        thisWeek: false,
        thisMonth: false,
        thisQuarter: false,
        thisYear: false,
      },
      ageRange: {
        min: "",
        max: "",
      },
      state: "",
      householdAssets: {
        min: "",
        max: "",
      },
    });
    setSearchTerm("");
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const filterClients = () => {
    logger.info("[DEBUG] Starting filterClients");
    logger.info("[DEBUG] clients array length:", clients?.length || 0);

    // If clients is not an array, return empty array
    if (!Array.isArray(clients)) {
      logger.info("[DEBUG] clients is not an array");
      return [];
    }

    const allMembers = clients.flatMap((client) => {
      logger.info(
        "[DEBUG] Processing client with household ID:",
        client.householdId || client.household_id,
      );

      if (!client) return [];

      // Handle both array of members and single client cases
      const members = client.members
        ? client.members.map((member) => {
            // Ensure firstName and lastName are set (might be in Name object)
            let firstName = member.firstName;
            let lastName = member.lastName;

            // Check if Name object exists and use it if needed
            if ((!firstName || !lastName) && member.Name) {
              firstName = firstName || member.Name.first_name;
              lastName = lastName || member.Name.last_name;
            }

            logger.info(
              "[DEBUG] Processing member:",
              member.user_id,
              firstName,
              lastName,
            );

            // Ensure contact object is properly structured
            if (!member.contact) {
              member.contact = {};
            }

            // Check for contact information in all possible locations
            const email =
              member.contact?.email ||
              member.email ||
              (member.ContactInformation
                ? member.ContactInformation.email
                : null);

            const phoneNumber =
              member.contact?.primary_number ||
              member.contact?.phone ||
              member.primary_number ||
              member.phone ||
              (member.ContactInformation
                ? member.ContactInformation.primary_number
                : null);

            // Log what we found
            logger.info("[DEBUG] Found email:", email);
            logger.info("[DEBUG] Found phone:", phoneNumber);

            // UPDATE: If we have a contact_id but no contact data yet, create a stub for it
            // This is a fallback so the modal code can find the contact_id, even if data wasn't pre-loaded
            if (member.contact_id && !email && !phoneNumber) {
              logger.info(
                "[DEBUG] Member has contact_id but no contact info, adding reference to contact object",
              );
              // Store the contact_id in the contact object
              member.contact.contact_id = member.contact_id;
            }

            // Update the contact object with found values
            if (email) {
              member.contact.email = email;
            }

            if (phoneNumber) {
              // Prefer primary_number for consistency with HouseholdProfile.js
              member.contact.primary_number = phoneNumber;
              // Also set phone as backup
              member.contact.phone = phoneNumber;
            }

            // For debugging - add mock data in development if still missing
            if (process.env.NODE_ENV !== "production") {
              // If still no email, add a mock one
              if (!member.contact.email) {
                logger.info("[DEBUG] Adding mock email");
                member.contact.email = `${firstName || "user"}.${lastName || member.user_id}@example.com`;
              }

              // If still no phone, add a mock one
              if (!member.contact.phone && !member.contact.primary_number) {
                logger.info("[DEBUG] Adding mock phone number");
                member.contact.phone = "(555) 123-4567";
                member.contact.primary_number = "(555) 123-4567";
              }
            }

            // Ensure firstName and lastName are on the root object
            member.firstName = firstName;
            member.lastName = lastName;

            const result = {
              ...member,
              firstName,
              lastName,
              householdId: client.householdId || client.household_id,
              Household: client.Household || client.household,
            };

            return result;
          })
        : [
            {
              ...client,
              householdId: client.householdId || client.household_id,
              Household: client.Household || client.household,
              firstName:
                client.firstName ||
                (client.Name ? client.Name.first_name : null),
              lastName:
                client.lastName || (client.Name ? client.Name.last_name : null),
              contact: {
                ...(client.contact || {}),
                email:
                  client.contact?.email ||
                  client.email ||
                  (client.ContactInformation
                    ? client.ContactInformation.email
                    : null),
                phone:
                  client.contact?.phone ||
                  client.phone ||
                  client.primary_number ||
                  (client.ContactInformation
                    ? client.ContactInformation.primary_number
                    : null),
                primary_number:
                  client.contact?.primary_number ||
                  client.primary_number ||
                  client.phone ||
                  (client.ContactInformation
                    ? client.ContactInformation.primary_number
                    : null),
                contact_id: client.contact?.contact_id || client.contact_id, // Ensure contact_id is available
              },
            },
          ];

      return members;
    });

    logger.info("[DEBUG] Total members processed:", allMembers.length);

    // Perform one last check to ensure all members have contact info
    const finalMembers = allMembers.map((member) => {
      // If we got this far and still don't have name information, use placeholder
      if (!member.firstName && !member.lastName) {
        member.firstName = "Client";
        member.lastName = `#${member.user_id}`;
      }

      // Check if this member is missing critical contact data
      if (
        !member.contact?.email &&
        !member.contact?.phone &&
        !member.contact?.primary_number &&
        !member.email &&
        !member.phone &&
        !member.primary_number
      ) {
        logger.info("[DEBUG] Member with missing contact info:", member);
      }

      return member;
    });

    logger.info("All members after processing:", finalMembers);

    // Apply filters to the processed members
    return finalMembers.filter((member) => {
      if (!member) return false;

      // Create a searchable string from all relevant fields
      const searchableFields = [
        member.firstName,
        member.lastName,
        member.email,
        member.Household?.addresses?.find(
          (addr) => addr.HouseholdAddresses?.is_primary,
        )?.city,
        member.Household?.addresses?.find(
          (addr) => addr.HouseholdAddresses?.is_primary,
        )?.state,
        member.householdId?.toString(),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      // Search term filtering
      if (searchTerm && !searchableFields.includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Age filtering
      const age = member.dateOfBirth ? calculateAge(member.dateOfBirth) : null;
      if (
        filters.ageRange.min &&
        age !== null &&
        age < parseInt(filters.ageRange.min)
      ) {
        return false;
      }
      if (
        filters.ageRange.max &&
        age !== null &&
        age > parseInt(filters.ageRange.max)
      ) {
        return false;
      }

      // State filtering
      const state = member.Household?.addresses?.find(
        (addr) => addr.HouseholdAddresses?.is_primary,
      )?.state;
      if (filters.state && state && state !== filters.state) {
        return false;
      }

      // If we get here, all filters passed
      return true;
    });
  };

  const sortClients = (filteredClients) => {
    return [...filteredClients].sort((a, b) => {
      switch (sortBy) {
        case "lastName": {
          const lastNameA = (
            a.lastName ||
            a.Name?.last_name ||
            ""
          ).toLowerCase();
          const lastNameB = (
            b.lastName ||
            b.Name?.last_name ||
            ""
          ).toLowerCase();

          if (lastNameA === lastNameB) {
            const firstNameA = (
              a.firstName ||
              a.Name?.first_name ||
              ""
            ).toLowerCase();
            const firstNameB = (
              b.firstName ||
              b.Name?.first_name ||
              ""
            ).toLowerCase();
            return firstNameA.localeCompare(firstNameB);
          }

          return lastNameA.localeCompare(lastNameB);
        }
        case "firstName": {
          const firstNameA = (
            a.firstName ||
            a.Name?.first_name ||
            ""
          ).toLowerCase();
          const firstNameB = (
            b.firstName ||
            b.Name?.first_name ||
            ""
          ).toLowerCase();
          return firstNameA.localeCompare(firstNameB);
        }
        case "age": {
          const ageA =
            a.dateOfBirth || a.date_of_birth
              ? calculateAge(a.dateOfBirth || a.date_of_birth)
              : 0;
          const ageB =
            b.dateOfBirth || b.date_of_birth
              ? calculateAge(b.dateOfBirth || b.date_of_birth)
              : 0;
          return ageB - ageA;
        }
        case "location": {
          const locationA = a.Household?.addresses?.find(
            (addr) => addr.HouseholdAddresses?.is_primary,
          )?.city
            ? `${a.Household.addresses.find((addr) => addr.HouseholdAddresses?.is_primary).city}, ${a.Household.addresses.find((addr) => addr.HouseholdAddresses?.is_primary).state}`.toLowerCase()
            : a.address
              ? `${a.address.city}, ${a.address.state}`.toLowerCase()
              : "";
          const locationB = b.Household?.addresses?.find(
            (addr) => addr.HouseholdAddresses?.is_primary,
          )?.city
            ? `${b.Household.addresses.find((addr) => addr.HouseholdAddresses?.is_primary).city}, ${b.Household.addresses.find((addr) => addr.HouseholdAddresses?.is_primary).state}`.toLowerCase()
            : b.address
              ? `${b.address.city}, ${b.address.state}`.toLowerCase()
              : "";
          return locationA.localeCompare(locationB);
        }
        default:
          return 0;
      }
    });
  };

  const filteredAndSortedClients = sortClients(filterClients());
  const totalPages = Math.ceil(
    filteredAndSortedClients.length / clientsPerPage,
  );
  const currentClients = filteredAndSortedClients.slice(
    (currentPage - 1) * clientsPerPage,
    currentPage * clientsPerPage,
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesShown = 5;
    const halfMaxPages = Math.floor(maxPagesShown / 2);

    let startPage = Math.max(1, currentPage - halfMaxPages);
    let endPage = Math.min(totalPages, startPage + maxPagesShown - 1);

    if (endPage - startPage + 1 < maxPagesShown) {
      startPage = Math.max(1, endPage - maxPagesShown + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) pageNumbers.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  // Communication handlers
  const handleCall = async (member) => {
    logger.info("[DEBUG] handleCall member:", member);
    logger.info("[DEBUG] member.contact:", member.contact);
    logger.info("[DEBUG] member direct fields:", {
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      phone: member.phone,
      primary_number: member.primary_number,
      contact_id: member.contact_id,
      user_id: member.user_id,
    });

    // Create a contact object if it doesn't exist
    if (!member.contact) {
      member.contact = {};
    }

    // Check in multiple possible locations for phone number
    let phoneNumber =
      member.contact?.primary_number ||
      member.contact?.phone ||
      member.primary_number ||
      member.phone ||
      (member.ContactInformation
        ? member.ContactInformation.primary_number
        : null);

    logger.info("[DEBUG] Found phone number:", phoneNumber);

    // If we have a contact_id but no phone, try to fetch the contact details
    const contactId = member.contact?.contact_id || member.contact_id;
    if (!phoneNumber && contactId) {
      logger.info(
        "[DEBUG] Attempting to fetch contact details using contact_id:",
        contactId,
      );
      try {
        const response = await apiConfig.api.get(`/contacts/${contactId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        logger.info("[DEBUG] Contact API response:", response.data);

        if (
          response.data &&
          (response.data.primary_number || response.data.phone)
        ) {
          phoneNumber = response.data.primary_number || response.data.phone;
          logger.info("[DEBUG] Found phone number from API:", phoneNumber);

          // Update the contact object with the retrieved data
          member.contact.primary_number = phoneNumber;
          member.contact.phone = phoneNumber;
          if (response.data.email) {
            member.contact.email = response.data.email;
          }
        }
      } catch (error) {
        logger.error("[DEBUG] Error fetching contact details:", error.message);
        logger.error("[DEBUG] Error details:", error);

        // Try a different endpoint as a fallback
        try {
          logger.info(
            "[DEBUG] Trying fallback contact endpoint using user_id:",
            member.user_id,
          );
          const fallbackResponse = await apiConfig.api.get(
            `/advisor-portal/client-database/contact-details/${member.user_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          logger.info("[DEBUG] Fallback API response:", fallbackResponse.data);

          if (
            fallbackResponse.data &&
            (fallbackResponse.data.primary_number ||
              fallbackResponse.data.phone)
          ) {
            phoneNumber =
              fallbackResponse.data.primary_number ||
              fallbackResponse.data.phone;
            logger.info(
              "[DEBUG] Found phone number from fallback API:",
              phoneNumber,
            );

            member.contact.primary_number = phoneNumber;
            member.contact.phone = phoneNumber;
            if (fallbackResponse.data.email) {
              member.contact.email = fallbackResponse.data.email;
            }
          }
        } catch (fallbackError) {
          logger.error(
            "[DEBUG] Fallback endpoint also failed:",
            fallbackError.message,
          );
        }
      }
    }

    if (!phoneNumber) {
      // For development/testing only - assign mock phone number
      if (process.env.NODE_ENV !== "production") {
        logger.info("[DEBUG] Adding mock phone number for development");
        member.contact.primary_number = "(555) 123-4567";
        member.contact.phone = "(555) 123-4567";
        setSelectedMember(member);
        setShowCallModal(true);
        return;
      }

      alert("No phone number available for this client");
      return;
    }

    // Add the phone number to the contact object
    member.contact.primary_number = phoneNumber;
    member.contact.phone = phoneNumber;

    setSelectedMember(member);
    setShowCallModal(true);
  };

  const handleEmail = async (member) => {
    logger.info("[DEBUG] handleEmail member:", member);
    logger.info("[DEBUG] member.contact:", member.contact);

    // Create a contact object if it doesn't exist
    if (!member.contact) {
      member.contact = {};
    }

    // Check in multiple possible locations for email
    let email =
      member.contact?.email ||
      member.email ||
      (member.ContactInformation ? member.ContactInformation.email : null);

    logger.info("[DEBUG] Found email:", email);

    // If we have a contact_id but no email, try to fetch the contact details
    const contactId = member.contact?.contact_id || member.contact_id;
    if (!email && contactId) {
      logger.info(
        "[DEBUG] Attempting to fetch contact details using contact_id:",
        contactId,
      );
      try {
        const response = await apiConfig.api.get(`/contacts/${contactId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        logger.info("[DEBUG] Contact API response:", response.data);

        if (response.data && response.data.email) {
          email = response.data.email;
          logger.info("[DEBUG] Found email from API:", email);

          // Update the contact object with the retrieved data
          member.contact.email = email;
          if (response.data.primary_number || response.data.phone) {
            member.contact.primary_number =
              response.data.primary_number || response.data.phone;
            member.contact.phone =
              response.data.primary_number || response.data.phone;
          }
        }
      } catch (error) {
        logger.error("[DEBUG] Error fetching contact details:", error);
      }
    }

    if (!email) {
      // For development/testing only - assign mock email
      if (process.env.NODE_ENV !== "production") {
        logger.info("[DEBUG] Adding mock email for development");
        const firstName = member.firstName || member.Name?.first_name || "user";
        const lastName =
          member.lastName || member.Name?.last_name || member.user_id;
        member.contact.email = `${firstName}.${lastName}@example.com`;
        setSelectedMember(member);
        setShowEmailModal(true);
        return;
      }

      alert("No email address available for this client");
      return;
    }

    // Add the email to the contact object
    member.contact.email = email;

    setSelectedMember(member);
    setShowEmailModal(true);
  };

  const handleMessage = async (member) => {
    logger.info("[DEBUG] handleMessage member:", member);

    // Create a contact object if it doesn't exist
    if (!member.contact) {
      member.contact = {};
    }

    // Check in multiple possible locations for phone number
    let phoneNumber =
      member.contact?.primary_number ||
      member.contact?.phone ||
      member.primary_number ||
      member.phone ||
      (member.ContactInformation
        ? member.ContactInformation.primary_number
        : null);

    logger.info("[DEBUG] Found phone number for messaging:", phoneNumber);

    // If we have a contact_id but no phone, try to fetch the contact details
    const contactId = member.contact?.contact_id || member.contact_id;
    if (!phoneNumber && contactId) {
      logger.info(
        "[DEBUG] Attempting to fetch contact details using contact_id:",
        contactId,
      );
      try {
        const response = await apiConfig.api.get(`/contacts/${contactId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        logger.info("[DEBUG] Contact API response:", response.data);

        if (
          response.data &&
          (response.data.primary_number || response.data.phone)
        ) {
          phoneNumber = response.data.primary_number || response.data.phone;
          logger.info("[DEBUG] Found phone number from API:", phoneNumber);

          // Update the contact object with the retrieved data
          member.contact.primary_number = phoneNumber;
          member.contact.phone = phoneNumber;
          if (response.data.email) {
            member.contact.email = response.data.email;
          }
        }
      } catch (error) {
        logger.error("[DEBUG] Error fetching contact details:", error.message);
        logger.error("[DEBUG] Error details:", error);

        // Try a different endpoint as a fallback
        try {
          logger.info(
            "[DEBUG] Trying fallback contact endpoint using user_id:",
            member.user_id,
          );
          const fallbackResponse = await apiConfig.api.get(
            `/advisor-portal/client-database/contact-details/${member.user_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          logger.info("[DEBUG] Fallback API response:", fallbackResponse.data);

          if (
            fallbackResponse.data &&
            (fallbackResponse.data.primary_number ||
              fallbackResponse.data.phone)
          ) {
            phoneNumber =
              fallbackResponse.data.primary_number ||
              fallbackResponse.data.phone;
            logger.info(
              "[DEBUG] Found phone number from fallback API:",
              phoneNumber,
            );

            member.contact.primary_number = phoneNumber;
            member.contact.phone = phoneNumber;
            if (fallbackResponse.data.email) {
              member.contact.email = fallbackResponse.data.email;
            }
          }
        } catch (fallbackError) {
          logger.error(
            "[DEBUG] Fallback endpoint also failed:",
            fallbackError.message,
          );
        }
      }
    }

    if (!phoneNumber) {
      // For development/testing only - assign mock phone number
      if (process.env.NODE_ENV !== "production") {
        logger.info("[DEBUG] Adding mock phone number for messaging");
        member.contact.primary_number = "(555) 123-4567";
        member.contact.phone = "(555) 123-4567";

        // Ensure we have all the required information for the selectedMember
        const memberForNavigate = {
          ...member,
          userId: member.user_id || member.userId,
          firstName: member.firstName || member.Name?.first_name || "Unknown",
          lastName: member.lastName || member.Name?.last_name || "Client",
          contact: {
            ...member.contact,
            primary_number: member.contact.primary_number,
            phone: member.contact.phone,
            email: member.contact.email || member.email,
          },
        };

        logger.info("[DEBUG] Navigating with member data:", memberForNavigate);

        navigate(
          `/advisor-portal/client-database/household-profile/${member.householdId}/communications`,
          {
            state: { activeTab: "text", selectedMember: memberForNavigate },
          },
        );
        return;
      }

      alert("No phone number available for this client");
      return;
    }

    // Add the phone number to the contact object
    member.contact.primary_number = phoneNumber;
    member.contact.phone = phoneNumber;

    // Ensure we have all the required information for the selectedMember
    const memberForNavigate = {
      ...member,
      userId: member.user_id || member.userId,
      firstName: member.firstName || member.Name?.first_name || "Unknown",
      lastName: member.lastName || member.Name?.last_name || "Client",
      contact: {
        ...member.contact,
        primary_number: member.contact.primary_number,
        phone: member.contact.phone,
        email: member.contact.email || member.email,
      },
    };

    logger.info("[DEBUG] Navigating with member data:", memberForNavigate);

    navigate(
      `/advisor-portal/client-database/household-profile/${member.householdId}/communications`,
      {
        state: { activeTab: "text", selectedMember: memberForNavigate },
      },
    );
  };

  const handleConfirmCall = () => {
    logger.info(
      "[DEBUG] handleConfirmCall for selectedMember:",
      selectedMember,
    );

    const phoneNumber =
      selectedMember?.contact?.primary_number ||
      selectedMember?.contact?.phone ||
      selectedMember?.primary_number ||
      selectedMember?.phone ||
      (selectedMember?.ContactInformation
        ? selectedMember.ContactInformation.primary_number
        : null);

    if (phoneNumber) {
      logger.info("[DEBUG] Initiating call to:", phoneNumber);
      window.location.href = `tel:${phoneNumber}`;
    } else {
      logger.info("[DEBUG] No phone number found for call");
    }

    setShowCallModal(false);
  };

  const handleSendEmail = (emailData) => {
    logger.info("Sending email:", emailData);
    // TODO: Implement email sending functionality
    setShowEmailModal(false);
  };

  const handleProfileClick = (client) => {
    try {
      // Get IDs directly from the client object
      const householdId = client.householdId || client.household_id;
      const userId = client.userId || client.user_id;

      if (!householdId || !userId) {
        logger.error("Missing required IDs:", { householdId, userId });
        return;
      }

      logger.info("Navigating to profile:", { householdId, userId });
      navigate(
        `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}`,
      );
    } catch (error) {
      logger.error("Error navigating to profile:", error);
    }
  };

  const renderClientCard = (client) => (
    <div
      key={client.id}
      className="bg-surface-default dark:bg-surface-default shadow rounded-lg p-6 mb-4 hover:shadow-md dark:shadow-none transition-shadow"
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {client.firstName && client.lastName
              ? `${client.firstName} ${client.lastName}`
              : `Client #${client.user_id}`}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Household ID: {client.householdId}
          </p>
          {client.email && (
            <p className="text-sm text-gray-500 dark:text-gray-500">{client.email}</p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <HealthScore score={client.healthScore || generateHealthScore()} />
          <RiskExposureChart
            riskScore={
              client.RiskToleranceAnalyses?.[0]?.desired_risk_number || 50
            }
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end space-x-2">
        <button
          onClick={() => handleProfileClick(client)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          View Profile
        </button>
      </div>
    </div>
  );

  return (
    <SubPageLayout
      title="Client Database"
      subtitle="View and manage your clients"
      breadcrumbItems={[
        { label: "Advisor Portal", path: "#" },
        { label: "Client Database", path: "/advisor-portal/clients" },
      ]}
    >
      <div className="p-4">
        <div className="space-y-4">
          {/* Search and Filters Section */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
                  >
                    {showFilters ? "Hide Filters" : "Show Filters"}
                  </button>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      </div>
                      <input
                        type="text"
                        placeholder="Search clients..."
                        className="pl-9 pr-4 py-1.5 w-64 text-sm rounded-lg border border-gray-300 dark:border-gray-600 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400
                                 focus:outline-none focus:ring-2 focus:ring-[#029831] dark:focus:ring-[#00ff9d] focus:border-transparent"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </div>
                    <button className="py-1.5 px-4 bg-[#202EC6] text-white rounded-lg hover:bg-[#1a237e] transition-colors text-sm">
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Panel (if shown) */}
          {showFilters && (
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 text-sm">Pipeline Stage</h3>
                  <div className="space-y-2">
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.pipelineStage.clients}
                        onChange={() =>
                          handleFilterChange("pipelineStage", "clients")
                        }
                      />
                      <span>Clients</span>
                    </label>
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.pipelineStage.futureClients}
                        onChange={() =>
                          handleFilterChange("pipelineStage", "futureClients")
                        }
                      />
                      <span>Future Clients</span>
                    </label>
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.pipelineStage.retired}
                        onChange={() =>
                          handleFilterChange("pipelineStage", "retired")
                        }
                      />
                      <span>Retired</span>
                    </label>
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.pipelineStage.highNetWorth}
                        onChange={() =>
                          handleFilterChange("pipelineStage", "highNetWorth")
                        }
                      />
                      <span>High net worth clients</span>
                    </label>
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.pipelineStage.rmdRequirement}
                        onChange={() =>
                          handleFilterChange("pipelineStage", "rmdRequirement")
                        }
                      />
                      <span>RMD Requirement</span>
                    </label>
                  </div>

                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 mt-6 text-sm">Last Contacted</h3>
                  <div className="space-y-2">
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.lastContacted.thisWeek}
                        onChange={() =>
                          handleFilterChange("lastContacted", "thisWeek")
                        }
                      />
                      <span>This Week</span>
                    </label>
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.lastContacted.thisMonth}
                        onChange={() =>
                          handleFilterChange("lastContacted", "thisMonth")
                        }
                      />
                      <span>This Month</span>
                    </label>
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.lastContacted.thisQuarter}
                        onChange={() =>
                          handleFilterChange("lastContacted", "thisQuarter")
                        }
                      />
                      <span>This Quarter</span>
                    </label>
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={filters.lastContacted.thisYear}
                        onChange={() =>
                          handleFilterChange("lastContacted", "thisYear")
                        }
                      />
                      <span>This Year</span>
                    </label>
                  </div>

                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 mt-6 text-sm">Additional Filters</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Age Range:</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          placeholder="Min"
                          className="p-1.5 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 
                                   border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400"
                          value={filters.ageRange.min}
                          onChange={(e) => handleRangeChange("ageRange", "min", e.target.value)}
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          className="p-1.5 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 
                                   border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400"
                          value={filters.ageRange.max}
                          onChange={(e) => handleRangeChange("ageRange", "max", e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">State:</label>
                      <select
                        className="w-full p-1.5 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 
                                 border-gray-300 dark:border-gray-600"
                        value={filters.state}
                        onChange={handleStateChange}
                      >
                        <option value="">Select State</option>
                        {/* ... state options ... */}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Household Assets:</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          placeholder="Min"
                          className="p-1.5 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 
                                   border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400"
                          value={filters.householdAssets.min}
                          onChange={(e) => handleRangeChange("householdAssets", "min", e.target.value)}
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          className="p-1.5 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 
                                   border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400"
                          value={filters.householdAssets.max}
                          onChange={(e) => handleRangeChange("householdAssets", "max", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    className="w-full py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg mt-6 
                             hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Client List Section */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                {/* Sort Controls */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <select
                      className="p-1.5 text-sm border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 
                     border-gray-300 dark:border-gray-600"
                      value={sortBy}
                      onChange={handleSortChange}
                    >
                      <option value="firstName">First Name</option>
                      <option value="lastName">Last Name</option>
                      <option value="age">Age</option>
                      <option value="location">Location</option>
                      <option value="lastContact">Last Contact</option>
                    </select>
                  </div>
                </div>

                {/* Client List */}
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <div className="p-4 text-center text-gray-700 dark:text-gray-300">
                      Loading clients...
                    </div>
                  ) : error ? (
                    <div className="p-4 text-center text-red-500">{error}</div>
                  ) : filteredAndSortedClients.length === 0 ? (
                    <div className="p-4 text-center text-gray-700 dark:text-gray-300">
                      {searchTerm || Object.values(filters).some((v) => v !== "")
                        ? "No clients match your search criteria. Try adjusting your filters."
                        : "No clients found"}
                    </div>
                  ) : (
                    currentClients.map((member, index) => {
                      // Extract name from either direct properties or Name object
                      const firstName =
                        member.firstName || member.Name?.first_name || "";
                      const lastName =
                        member.lastName || member.Name?.last_name || "";
                      const fullName = `${firstName} ${lastName}`.trim();

                      // Calculate age from either dateOfBirth or date_of_birth
                      const birthDate = member.dateOfBirth || member.date_of_birth;
                      const age = birthDate ? calculateAge(birthDate) : "";

                      // Get location from either Address or nested structure
                      const location = (() => {
                        // Try to get the primary address from the Household
                        const address = member.Household
                          ? getPrimaryAddress(member.Household)
                          : null;

                        if (address) {
                          // For display in the UI, only show city, state
                          return [address.city, address.state]
                            .filter(Boolean)
                            .join(", ");
                        }

                        // Fallback to old structure if it exists
                        if (member.Household?.Address) {
                          return [
                            member.Household.Address.city,
                            member.Household.Address.state,
                          ]
                            .filter(Boolean)
                            .join(", ");
                        }

                        if (member.address) {
                          return [member.address.city, member.address.state]
                            .filter(Boolean)
                            .join(", ");
                        }

                        return "No address available";
                      })();

                      const healthScore = generateHealthScore();

                      return (
                        <div
                          key={`${member.householdId || member.household_id}-${member.userId || member.user_id}-${index}`}
                          className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 p-3 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            {/* Left Section - Icon and Client Details */}
                            <div className="flex items-center gap-2 min-w-[250px] pl-1">
                              <div
                                className="w-[48px] h-[48px] flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600 
                               dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                onClick={() =>
                                  navigate(
                                    `/advisor-portal/client-database/household-profile/${member.householdId || member.household_id}`,
                                  )
                                }
                                title="Household Profile"
                              >
                                <svg
                                  className="w-[28px] h-[28px]"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                </svg>
                              </div>
                              <div className="flex flex-col justify-center">
                                <div
                                  className="text-base font-medium text-gray-900 dark:text-gray-100 cursor-pointer hover:text-blue-600 
                                 dark:hover:text-blue-400 leading-tight group relative inline-block"
                                  onClick={() => handleProfileClick(member)}
                                  title="Client Profile"
                                >
                                  {fullName || `Client #${member.user_id}`}
                                  {age && (
                                    <span className="text-gray-600 dark:text-gray-400 ml-1">{age}</span>
                                  )}
                                </div>
                                <div
                                  className="text-sm text-gray-500 dark:text-gray-400 leading-tight mt-1 cursor-pointer 
                                 hover:text-gray-700 dark:hover:text-gray-300 group relative inline-block"
                                  onClick={(e) => {
                                    if (location !== "No address available") {
                                      // Get full address for clipboard
                                      let fullAddress = "";
                                      const address = member.Household
                                        ? getPrimaryAddress(member.Household)
                                        : null;

                                      logger.info(
                                        "[DEBUG] Copy address clicked for:",
                                        member.firstName,
                                        member.lastName,
                                      );
                                      logger.info(
                                        "[DEBUG] Found address:",
                                        address,
                                      );

                                      if (address) {
                                        // Build address with null checks for each component
                                        const addressComponents = [];

                                        // Add address lines
                                        if (address.address_line1) {
                                          addressComponents.push(
                                            address.address_line1,
                                          );
                                        }

                                        if (address.address_line2) {
                                          addressComponents.push(
                                            address.address_line2,
                                          );
                                        }

                                        // Add city, state, zip line
                                        const cityStateZip = [
                                          address.city,
                                          address.state,
                                          address.zip,
                                        ]
                                          .filter(Boolean)
                                          .join(", ");

                                        if (cityStateZip) {
                                          addressComponents.push(cityStateZip);
                                        }

                                        fullAddress = addressComponents.join("\n");
                                        logger.info(
                                          "[DEBUG] Formatted address:",
                                          fullAddress,
                                        );
                                      } else if (
                                        location !== "No address available"
                                      ) {
                                        // Just use the location string if we don't have full address
                                        fullAddress = location;
                                        logger.info(
                                          "[DEBUG] Using location as fallback:",
                                          location,
                                        );
                                      }

                                      // If we still don't have an address, use a default message
                                      if (!fullAddress) {
                                        fullAddress = location;
                                        logger.info(
                                          "[DEBUG] No address constructed, using location:",
                                          location,
                                        );
                                      }

                                      // Use our safe copy function instead of direct navigator.clipboard
                                      logger.info(
                                        "[DEBUG] Attempting to copy to clipboard:",
                                        fullAddress,
                                      );
                                      copyTextToClipboard(fullAddress);

                                      // Show brief feedback
                                      const target = e.currentTarget;
                                      const originalText = target.textContent;
                                      target.textContent = "Address copied!";
                                      setTimeout(() => {
                                        target.textContent = originalText;
                                      }, 1500);
                                    }
                                  }}
                                  title={
                                    location !== "No address available"
                                      ? "Copy Address"
                                      : "No address available"
                                  }
                                >
                                  {location}
                                </div>
                              </div>
                            </div>

                            {/* Middle Section - Task Tags */}
                            <div className="flex flex-wrap gap-1.5 max-w-[400px]">
                              {generateUpcomingTasks(member).map((tag, index) => (
                                <span
                                  key={index}
                                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                    tag.includes("RMD")
                                      ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400"
                                      : tag.includes("Turning")
                                        ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                        : tag.includes("Medicare")
                                          ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
                                          : tag.includes("Rebalance")
                                            ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                            : tag.includes("Birthday")
                                              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                                              : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                  } border border-opacity-50 ${
                                    tag.includes("RMD")
                                      ? "border-orange-200 dark:border-orange-500/20"
                                      : tag.includes("Turning")
                                        ? "border-green-200 dark:border-green-500/20"
                                        : tag.includes("Medicare")
                                          ? "border-purple-200 dark:border-purple-500/20"
                                          : tag.includes("Rebalance")
                                            ? "border-red-200 dark:border-red-500/20"
                                            : tag.includes("Birthday")
                                              ? "border-blue-200 dark:border-blue-500/20"
                                              : "border-gray-200 dark:border-gray-600"
                                  }`}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>

                            {/* Right Section - Health Score, Risk Profile, and Actions */}
                            <div className="flex items-center gap-6">
                              <div className="flex items-center">
                                <HealthScore score={healthScore} />
                                <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 mx-4 flex-shrink-0"></div>
                                <RiskExposureChart
                                  riskScore={
                                    member.RiskToleranceAnalyses?.[0]
                                      ?.desired_risk_number || 50
                                  }
                                />
                              </div>

                              <div className="flex flex-col gap-1 ml-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMessage(member);
                                  }}
                                  className="text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 p-1 rounded-full 
                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  title="Message"
                                >
                                  <svg
                                    className="w-4 h-4 transform transition-all duration-150 hover:scale-125"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCall(member);
                                  }}
                                  className="text-gray-400 hover:text-green-600 dark:hover:text-green-400 p-1 rounded-full 
                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  title="Call"
                                >
                                  <svg
                                    className="w-4 h-4 transform transition-all duration-150 hover:scale-125"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEmail(member);
                                  }}
                                  className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 rounded-full 
                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  title="Email"
                                >
                                  <svg
                                    className="w-4 h-4 transform transition-all duration-150 hover:scale-125"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Pagination */}
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Showing{" "}
                        <span className="font-medium">
                          {(currentPage - 1) * clientsPerPage + 1}
                        </span>{" "}
                        -{" "}
                        <span className="font-medium">
                          {Math.min(
                            currentPage * clientsPerPage,
                            filteredAndSortedClients.length,
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium">
                          {filteredAndSortedClients.length}
                        </span>{" "}
                        clients
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-2 py-1 rounded-md text-sm ${
                          currentPage === 1
                            ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                        }`}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>

                      {getPageNumbers().map((pageNum, index) =>
                        pageNum === "..." ? (
                          <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-500 dark:text-gray-400">
                            ...
                          </span>
                        ) : (
                          <button
                            key={`page-${pageNum}`}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-1 rounded-md text-sm ${
                              currentPage === pageNum
                                ? "bg-blue-600 text-white"
                                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                            }`}
                          >
                            {pageNum}
                          </button>
                        ),
                      )}

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-2 py-1 rounded-md text-sm ${
                          currentPage === totalPages
                            ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                        }`}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
      />
      <EmailComposerModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        client={selectedMember}
        onSend={handleSendEmail}
      />
      <CallConfirmationModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        client={selectedMember}
        onConfirm={handleConfirmCall}
      />
    </SubPageLayout>
  );
};

export default Clients;
